import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { VoiceProvider } from './contexts/VoiceContext';
import { api } from './api/client';
import { warmConversationIds } from './api/community-hooks';
import { getActiveConversationId } from './api/community-realtime';
import { ErrorBoundary } from './components/ErrorBoundary';
import { startUpdateChecker } from '@/lib/app-update';
import { attachNavTracker } from './lib/tracking/nav-tracker';
import { routeTree } from './routeTree.gen';
import './styles/globals.css';

// Debug gate — used for both eruda (visible console) and memory monitor.
// Enable via VITE_ENABLE_ERUDA=true OR ?debug=true query param.
const DEBUG_ENABLED =
  import.meta.env.VITE_ENABLE_ERUDA === 'true' ||
  new URLSearchParams(window.location.search).has('debug');

// Mobile debug console
if (DEBUG_ENABLED) {
  import('eruda').then((eruda) => eruda.default.init());
}

// Vite emits `vite:preloadError` when a dynamic import() can't resolve — most
// commonly after a deploy when the cached HTML still references a chunk hash
// the server no longer has, or when a chunk is evicted from the SW cache.
// Auto-recover by reloading once per session; the next load gets fresh
// HTML + fresh hash references and resolves. The sessionStorage guard
// prevents an infinite reload loop if the failure is something other than
// stale hashes (e.g. genuine offline with no cache).
window.addEventListener('vite:preloadError', (event) => {
  const RELOAD_KEY = 'fb-preload-reload-attempted';
  if (sessionStorage.getItem(RELOAD_KEY)) {
    console.error('[preloadError] already reloaded once, surfacing error', event);
    return;
  }
  sessionStorage.setItem(RELOAD_KEY, '1');
  event.preventDefault();
  // A failing chunk can be a POISONED immutable browser-cache entry (the SPA
  // HTML fallback cached under the /assets URL during a deploy window — see
  // the index.html boot-guard). location.reload() alone never revalidates an
  // immutable entry; a cache-bypassing refetch of the exact URL overwrites it.
  const failedUrl = String((event as unknown as { payload?: Error }).payload?.message ?? '')
    .match(/https?:\/\/\S+\.js/)?.[0];
  const repair = failedUrl ? fetch(failedUrl, { cache: 'reload' }).catch(() => {}) : Promise.resolve();
  void repair.then(() => window.location.reload());
});

// Evaluating this far means the initial bundle resolved — reset the
// index.html boot-guard's one-shot flag so the NEXT deploy that strands this
// session gets a fresh retry. Deliberately NOT clearing the preload flag
// above: lazy-chunk failures happen after successful boots, so clearing it
// here would re-arm an infinite boot→fail→reload loop.
try {
  sessionStorage.removeItem('fb-boot-reload-attempted');
} catch { /* storage unavailable — nothing to reset */ }

// Global unhandled promise rejection tracking — max 1 per 10 seconds to prevent hammering the edge function
let _lastRejectionLog = 0;
window.addEventListener('unhandledrejection', (event) => {
  const now = Date.now();
  if (now - _lastRejectionLog < 10_000) return;
  _lastRejectionLog = now;
  api.logActivity({
    type: 'navigation',
    action: 'error',
    entity_type: 'page',
    entity_id: window.location.pathname,
    entity_name: 'UnhandledRejection',
    description: String(event.reason).substring(0, 200),
    metadata: {},
  }).catch(() => {});
});

// Create a query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Default OFF (2026-07-02): every focus/tab-switch was refetching every
      // mounted query — with staleTime 5min most were no-ops, but user-gated
      // reads (profile, memberships, roles, learn/planner data) still hit the
      // network on every window regain. Realtime + reconcile keep chat live, so
      // focus-refetch is opt-in only where staleness is user-visible: the three
      // community hooks (conversations, unread counts, stories) set it to true
      // explicitly. Admin routes previously each set this false to work around
      // the old default; those are now redundant no-ops (harmless).
      refetchOnWindowFocus: false,
      // Retry hygiene (2026-06-15): cap retries low and never retry 4xx. During the
      // outage, clients retried 5xx/522 on a synchronized backoff and re-stampeded the
      // recovering origin (and deepened 429 rate-limiting). Skip client errors entirely.
      retry: (failureCount, error) => {
        if (failureCount >= 2) return false;
        const status =
          (error as any)?.status ?? (error as any)?.statusCode ?? (error as any)?.code;
        if (typeof status === 'number' && status >= 400 && status < 500) return false;
        return true;
      },
      // Exponential backoff (1s, 2s, 4s…) capped at 30s, with ±50% jitter so a fleet
      // of clients doesn't retry in lockstep when the origin comes back.
      retryDelay: (attemptIndex) => {
        const base = Math.min(1000 * 2 ** attemptIndex, 30_000);
        return Math.round(base * (0.5 + Math.random() / 2));
      },
    },
  },
});

// Persist community message + alfie queries to IndexedDB so chats load
// instantly on PWA reopen. The PersistQueryClientProvider below blocks
// children render until rehydration resolves — without this, React mounts
// before IDB hydration completes and useMessages fires a fresh fetch while
// the persisted cache is still landing.
//
// The persisted copy is for instant paint ONLY — it is NOT treated as fresh.
// Alfie threads reconcile via useThreadMessages (staleTime:0 +
// refetchOnMount:'always'), and every send appends the new exchange straight
// into the ['alfie-messages', threadId] cache so the paint is already correct
// before that refetch lands; community messages reconcile over realtime. buster
// invalidates on schema changes; maxAge evicts anything older than 24h.
const persister = createAsyncStoragePersister({
  storage: {
    getItem: (key: string) => idbGet<string>(key).then((v: string | undefined) => v ?? null),
    setItem: (key: string, value: string) => idbSet(key, value),
    removeItem: (key: string) => idbDel(key),
  },
  key: 'fb-message-cache',
  // 5s (was 2s): with the dehydrate set bounded to the warm conversations below,
  // each write is small + flat, so a longer throttle keeps serialization off the
  // interaction hot path without losing data (a flush still runs on unload).
  throttleTime: 5_000,
});

const persistOptions = {
  persister,
  maxAge: 24 * 60 * 60_000,
  buster: import.meta.env.VITE_BUILD_VERSION || 'v1',
  dehydrateOptions: {
    shouldDehydrateQuery: (query: { queryKey: readonly unknown[] }) => {
      const key = query.queryKey;
      // Community messages: persist ONLY the warm working set (top-10 by recency
      // + the active conversation), not every conversation ever opened. This is
      // the bound that keeps the IndexedDB serialization flat over a session and
      // fixes the progressive interaction lag. The active id is restored on
      // refresh; warm ids switch instantly; everything else reconciles live.
      if (key[0] === 'community' && key[1] === 'messages') {
        const convId = key[2] as string | undefined;
        return (
          !!convId &&
          (warmConversationIds.has(convId) || convId === getActiveConversationId())
        );
      }
      return (
        key[0] === 'alfie-threads' ||
        key[0] === 'alfie-messages' ||
        key[0] === 'alfie-user-context' ||
        key[0] === 'community-channels' ||
        // The Xero account conversation. Bounded by construction — ONE key holding one thread,
        // not one per account — so it adds a flat, small entry rather than growing with the chart.
        // See `hooks/use-xero-chat-thread.ts`; the key is exported there, keep the two in step.
        key[0] === 'xero-account-chat'
      );
    },
  },
};

// Memory monitor — snapshots heap / DOM / React Query cache / realtime channels
// every 5 seconds. Only runs when debug is enabled, so zero production impact.
// Output visible in eruda console on mobile. See lib/debug/memory-monitor.ts.
if (DEBUG_ENABLED) {
  import('@/lib/debug/memory-monitor').then(({ startMemoryMonitor }) => {
    startMemoryMonitor({ queryClient });
  });
}

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
  defaultPendingMs: 200, // Only show loading state if navigation takes >200ms
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

attachNavTracker(router);

// Register the service worker — Web Push, plus an offline page. As of v8
// (2026-08-18) sw.js no longer caches ASSETS: that duplicated what the browser's
// HTTP cache already does for immutable /static/*, while adding a private,
// per-profile, reload-proof copy that could go bad invisibly. Its fetch handler
// now touches navigations only, and only to substitute offline.html when the
// network is unreachable. Repeat-load speed is unchanged. Do not re-add asset
// caching here without reading the header comment in sw.js first.
//
// An installed PWA typically RESUMES its page on relaunch
// instead of navigating, so the tab keeps running the old JS even after the new
// worker activates. startUpdateChecker() closes that gap by comparing the running
// bundle's version against /version.json and offering a reload.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      // updateViaCache:'none' — the default ('imports') bypasses the HTTP cache
      // for sw.js itself but NOT for what it importScripts(). sw-env.js is a
      // fixed url carrying the Supabase credentials the push beacon uses, so a
      // cached copy survives redeploys and credential rotations. 'none' makes
      // the browser revalidate the worker and its imports together.
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .catch((err) => console.warn('[sw] register failed', err));
  });
}
startUpdateChecker();

// The router mounts immediately. Routes that need an authenticated session
// drive their own loading state via __root.tsx's beforeLoad and per-route
// guards. The pre-React HTML shell in index.html paints during JS download;
// blocking the whole app on a global auth spinner would replace that shell
// with a worse one.
function InnerApp() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
      <SettingsProvider>
        <VoiceProvider>
          <AuthProvider>
            <InnerApp />
          </AuthProvider>
        </VoiceProvider>
      </SettingsProvider>
      {/* bottom-left for the same reason as the router devtools: the default
          bottom-right corner belongs to the Alfie launcher. */}
      {import.meta.env.DEV && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
      )}
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
